import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useMySystemPermissions from '../../hooks/useMySystemPermissions';

// `anyCapability` (M004-F0764, WebUI #237): ALSO admit a caller whose own system-capability
// map (GET /users/me/system-permissions) holds any of the named capabilities, decided from
// the capability read and never from a role name. Fails closed.
const ProtectedRoute = ({ children, roles = [], anyCapability = [] }) => {
  const { isAuthenticated, user, loading, sessionUnavailable, sessionError, retrySession } = useAuth();
  const location = useLocation();
  const roleAllows = roles.length === 0
    || roles.includes(user?.role)
    || (roles.includes('admin') && user?.role === 'super_admin');
  const needsCapabilityRead = anyCapability.length > 0 && isAuthenticated && !roleAllows;
  const { permissions: capabilityMap, ready: capabilitiesReady } = useMySystemPermissions(needsCapabilityRead);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // M004-F0626 / WEB-SESSION-002, WEB-SESSION-003: the app could not CONFIRM the
  // session — a 429, a 5xx or a dropped connection — but nothing said the token was
  // invalid, so it was kept. Showing the login form here would be a lie: the person
  // is not signed out, and silently doing it is why this defect went unreported for
  // as long as it did. Say what happened and offer the retry instead.
  if (!isAuthenticated && sessionUnavailable) {
    return (
      <div className="loading-screen" role="alert" aria-live="assertive">
        <h1>We could not confirm your session</h1>
        <p>{sessionError || 'StudioAEye did not answer in time. You are still signed in.'}</p>
        <button type="button" onClick={retrySession}>Retry</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user?.role;
  const currentPath = location.pathname;

  // Simple Room Controls users can ONLY access /app/room-controls
  if (userRole === 'simple_room_controls') {
    const allowedPaths = ['/app/room-controls'];
    const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/app/room-controls" replace />;
    }
  }

  // Event Simple users can ONLY access /app/event-simple
  // Redirect them away from any other page
  if (userRole === 'event_simple') {
    const allowedPaths = ['/app/event-simple'];
    const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
    if (!isAllowed) {
      return <Navigate to="/app/event-simple" replace />;
    }
  }

  // Check if user has required role (super_admin can access admin routes)
  const hasCapability = anyCapability.some((name) => capabilityMap?.[name] === true);
  if (needsCapabilityRead && !capabilitiesReady) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  const hasAccess = roleAllows || hasCapability;

  if (!hasAccess) {
    // Redirect to appropriate dashboard based on role
    // Event Simple is the default home page for Admin, Teacher, and Event Simple users
    if (userRole === 'admin' || userRole === 'super_admin') {
      return <Navigate to="/app/event-simple" replace />;
    }
    if (userRole === 'teacher') {
      return <Navigate to="/app/event-simple" replace />;
    }
    if (userRole === 'event_simple') {
      return <Navigate to="/app/event-simple" replace />;
    }
    if (userRole === 'simple_room_controls') {
      return <Navigate to="/app/room-controls" replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return children;
};

export default ProtectedRoute;
